import React, { useCallback, useEffect, useMemo, useState } from "react";
import { chunk, map, range, toString } from "lodash";
import { Moment } from "moment-timezone";
import styled, { css } from "styled-components";
import { formatDate } from "@backpacker/date";
import { getMonths, getWeekdays } from "@backpacker/date";
import {
  FontFamily,
  FontWeight,
  ZIndexes,
  content,
  dominant,
  form,
  transparentize,
} from "@glossy";
import { Translate } from "@linkfluence/i18n";
import { Range } from "@linkfluence/models/date";
import { Interval } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import {
  endOf,
  isAfter,
  isBefore,
  isBetween,
  isSame,
  mmt,
  next,
  previous,
  startOf,
  tz,
} from "@tootools/date";
import Icon from "@ui-kit/icon";
import Date from "../date";

type Period = Omit<Range, "interval">;

interface IProps {
  limits: Period;
  period: Period;
  interval: Interval;
  customHour?: boolean;
  type?: "start" | "end" | "standalone";
  locale: Locale;
  timezone: Timezone;
  onChange: (period: Omit<Range, "interval">) => void;
  className?: string;
  allowBeyondLimit?: boolean;
}

const DAYS_IN_WEEK = 7;
const NUMBER_OF_LINE = 6;
const ITEM_RADIUS = 15;

const Select = styled.select`
  background: ${(props) => transparentize(0.85, form(props, "primary"))};
  border-radius: 4px;
  color: ${(props) => content(props, "secondary")};
  height: 24px;
  width: 60px;
  font-family: ${FontFamily.Sans};
  font-size: 12px;
`;

const Header = styled.div`
  line-height: 20px;
  font-weight: bold;
  padding: 0 24px 24px 0;
  color: ${(props) => content(props, "secondary")};
`;

const StyledTranslate = styled(Translate)`
  font-weight: ${FontWeight.Bold};
  color: ${content};
`;

const Title = styled.div`
  font-weight: ${FontWeight.Bold};
  color: ${content};
  margin-right: 6px;
  margin-top: 1.5px;
`;

const Picker = styled.div`
  overflow: hidden;
  font-size: 12px;
  color: ${form};
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  padding-top: 15px;
`;

const Nav = styled.div`
  font-weight: bold;
  text-align: center;
`;

const PrevNext = styled.div`
  display: inline-block;
  width: 32px;
  cursor: pointer;
`;

const Current = styled.div`
  width: calc(100% - 64px);
  font-weight: ${FontWeight.Bold};
  font-size: 14px;
  display: inline-block;
  color: ${form};
`;

const Body = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 0;
`;

const WeekDay = styled.div`
  position: relative;
  display: inline-block;
  text-align: center;
  width: 30px;
  line-height: 28px;
  margin: 3px 0;
  color: ${(props) => content(props, "secondary")};
`;

const Month = styled(Date)`
  width: 33%;
`;

const Root = styled.div`
  overflow: hidden;
  font-size: 14px;
  width: 210px;
`;

const Day = styled(Date)``;

const Row = styled.div<{ week?: boolean }>`
  ${/* sc-selector */ Day},
  ${/* sc-selector */ Month} {
    &:last-child {
      border-top-right-radius: 14px;
      border-bottom-right-radius: 14px;
    }
    &:first-child {
      border-top-left-radius: 14px;
      border-bottom-left-radius: 14px;
    }
  }

  ${(props) =>
    props.week &&
    css`
      position: relative;
      &:hover:before {
        content: "";
        position: absolute;
        right: 0;
        bottom: 3px;
        top: 3px;
        cursor: pointer;
        border-radius: ${ITEM_RADIUS}px;
        z-index: ${ZIndexes.Menus};
        left: 0;
        border: 1px solid ${dominant};
      }
    `}
`;

const getPageInterval = (interval: Interval) =>
  interval === Interval.Month ? Interval.Year : Interval.Month;

const getCalendarTitle = (date: Moment, interval: Interval, locale: Locale) =>
  interval === Interval.Month
    ? "" + date.year()
    : formatDate(date, locale, "MMMM YYYY");

export const getStateFromDate = (
  date: Moment,
  selectedDate: Moment,
  currentDate: Moment,
  period: Period,
  limits: Period,
  isMonthly: boolean,
  allowBeyondLimit: boolean
) => {
  if (isMonthly) {
    if (isSame(startOf(selectedDate, Interval.Month), date)) {
      return "selected" as const;
      // Is in period
    }
    if (
      isBetween(
        date,
        startOf(period.from, Interval.Month),
        startOf(period.to, Interval.Month)
      )
    ) {
      return "active";
    }
    return "default" as const;
  }
  // Is out of the limits
  if (isBefore(date, limits.from)) {
    return "disabled" as const;
  }
  if (isAfter(date, limits.to) && !allowBeyondLimit) {
    return "disabled" as const;
  }
  if (date.month() !== currentDate.month()) {
    return "hidden" as const;
  }
  if (isSame(startOf(selectedDate, Interval.Day), date)) {
    return "selected" as const;
    // Is in period
  }
  if (
    isBetween(
      date,
      startOf(period.from, Interval.Day),
      endOf(period.to, Interval.Day)
    )
  ) {
    return "active";
  }
  return "default" as const;
};

const isStart = (date: Moment, period: Period, isMonthly: boolean) => {
  if (isMonthly) {
    return isSame(
      startOf(period.from, Interval.Month),
      startOf(date, Interval.Month)
    );
  }
  // Is first day of month
  if (date.date() === 1) {
    return true;
    // Is first day of period
  }
  return !!isSame(startOf(period.from, Interval.Day), date);
};

const isEnd = (date: Moment, period: Period, isMonthly: boolean) => {
  if (isMonthly) {
    return isSame(
      endOf(period.to, Interval.Month),
      endOf(date, Interval.Month)
    );
  }
  // Is last day of month
  if (date.date() === date.daysInMonth()) {
    return true;
    // Is last day of period
  }
  return !!isSame(startOf(period.to, Interval.Day), date);
};

const getCurrentHour = (date: Moment) => {
  const hour = date.hour();
  if (hour === 23 && date.minute() === 59) {
    return 24;
  }
  return hour;
};

const getHourLabel = (index: number) =>
  `${index < 10 ? `0${index}` : index === 24 ? "23" : index.toString()}h${
    index === 24 ? "59" : "00"
  }`;

const useCalendar = (
  period: Period,
  timezone: Timezone,
  type: "end" | "start" | "standalone",
  interval: Interval,
  onChange: (period: Period) => void
) => {
  const selectedPeriod = useMemo(
    () => ({
      from: tz(period.from, timezone),
      to: tz(period.to, timezone),
    }),
    [period.from, period.to, timezone]
  );
  const selectedDate = type === "end" ? selectedPeriod.to : selectedPeriod.from;
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const isMonthly = interval === Interval.Month;
  const startDate = useMemo(
    () =>
      isMonthly
        ? startOf(currentDate, Interval.Year)
        : startOf(startOf(currentDate, Interval.Month), Interval.Week),
    [currentDate, isMonthly]
  );
  const isWeekly = interval === Interval.Week;
  const currentInterval =
    interval === Interval.Month ? Interval.Month : Interval.Day;

  useEffect(() => {
    setCurrentDate(currentDate);
  }, [currentDate, selectedDate]);

  const previousPage = useCallback(() => {
    setCurrentDate(previous(currentDate, 1, getPageInterval(interval)));
  }, [currentDate, interval]);

  const nextPage = useCallback(() => {
    setCurrentDate(next(currentDate, 1, getPageInterval(interval)));
  }, [currentDate, interval]);

  const onChangePeriod = useCallback(
    (nextPartialPeriod: Partial<Period>) => {
      const nextPeriod = { ...selectedPeriod, ...nextPartialPeriod };
      if (isAfter(nextPeriod.from, nextPeriod.to)) {
        const _interval =
          interval === Interval.Week ? interval : currentInterval;
        if (type === "end") {
          onChange({
            from: startOf(nextPeriod.to, _interval).toDate(),
            to: mmt(nextPeriod.to).toDate(),
          });
        } else {
          onChange({
            from: mmt(nextPeriod.from).toDate(),
            to: endOf(nextPeriod.from, _interval).toDate(),
          });
        }
      } else {
        onChange({
          from: mmt(nextPeriod.from).toDate(),
          to: mmt(nextPeriod.to).toDate(),
        });
      }
    },
    [currentInterval, interval, onChange, selectedPeriod, type]
  );

  const onChangeDate = useCallback(
    (date: Moment) => {
      const _interval = interval === Interval.Week ? interval : currentInterval;
      if (type === "end") {
        onChangePeriod({
          to: endOf(date, _interval).toDate(),
        });
      } else if (type === "start") {
        onChangePeriod({
          from: startOf(date, _interval).toDate(),
        });
      } else {
        onChangePeriod({
          from: startOf(date, _interval).toDate(),
          to: endOf(date, _interval).toDate(),
        });
      }
    },
    [currentInterval, interval, onChangePeriod, type]
  );

  const selectWeek = useCallback(
    (index: number): void => {
      if (isWeekly) {
        onChangeDate(next(startDate, index * DAYS_IN_WEEK, "day"));
      }
    },
    [isWeekly, onChangeDate, startDate]
  );

  const onClickDay = useCallback(
    (date: Moment) => {
      if (!isWeekly) {
        onChangeDate(date);
      }
    },
    [isWeekly, onChangeDate]
  );

  const onChangeHours = useCallback(
    (e) => {
      const hour = parseInt(e.target.value);
      const nextDate =
        hour === 24
          ? endOf(selectedDate, Interval.Day)
          : next(startOf(selectedDate, Interval.Day), hour, Interval.Hour);
      if (type === "end") {
        onChangePeriod({
          to: nextDate.toDate(),
        });
      } else if (type === "start") {
        onChangePeriod({
          from: nextDate.toDate(),
        });
      } else {
        onChangePeriod({
          from: nextDate.toDate(),
          to: nextDate.toDate(),
        });
      }
    },
    [onChangePeriod, selectedDate, type]
  );

  const onMouseDownHours = useCallback(
    (event) => {
      // RADAR-6158: fix for Firefox which does not receive the new selected value on change
      toString(selectedDate.hour()) !== event.target.value &&
        onChangeHours(event);
    },
    [selectedDate, onChangeHours]
  );

  return {
    currentDate,
    currentInterval,
    isWeekly,
    isMonthly,
    previousPage,
    nextPage,
    onChangeDate,
    onChangeHours,
    onClickDay,
    onMouseDownHours,
    selectedDate,
    selectedPeriod,
    selectWeek,
    startDate,
  };
};

const UiKitPeriodSelectorCalendar = ({
  limits,
  period,
  interval,
  customHour,
  type = "standalone",
  locale,
  timezone,
  onChange,
  className,
  allowBeyondLimit = false,
}: IProps) => {
  const {
    currentDate,
    currentInterval,
    isWeekly,
    isMonthly,
    previousPage,
    nextPage,
    onChangeDate,
    onChangeHours,
    onClickDay,
    onMouseDownHours,
    selectedDate,
    selectedPeriod,
    selectWeek,
    startDate,
  } = useCalendar(period, timezone, type, interval, onChange);

  return (
    <Root className={className}>
      <Header>
        {type !== "standalone" && (
          <StyledTranslate
            message={
              type === "start" ? "DATES::CUSTOM::FROM" : "DATES::CUSTOM::TO"
            }
            locale={locale}
          />
        )}
        <div style={{ display: "flex" }}>
          <Title>{formatDate(selectedDate, locale, "ll")}</Title>
          {customHour && (
            <div onMouseDown={onMouseDownHours}>
              <Select
                onChange={onChangeHours}
                value={getCurrentHour(selectedDate)}
              >
                {map(range(type === "end" ? 25 : 24), (i) => (
                  <option key={i} value={i}>
                    {getHourLabel(i)}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </Header>
      <Picker>
        <Nav>
          <PrevNext onClick={previousPage}>
            <Icon glyph="chevron-left" size="xs" />
          </PrevNext>
          <Current>
            {getCalendarTitle(currentDate, currentInterval, locale)}
          </Current>
          <PrevNext onClick={nextPage}>
            <Icon glyph="chevron-right" size="xs" />
          </PrevNext>
        </Nav>
        <Body>
          {isMonthly ? (
            <div>
              {map(chunk(getMonths(locale, true), 3), (months, i) => (
                <Row week={isWeekly} key={i}>
                  {map(months, (month, j) => {
                    const mDate = next(startDate, i * 3 + j, Interval.Month);
                    return (
                      <Month
                        key={j}
                        onClick={() => onChangeDate(mDate)}
                        state={getStateFromDate(
                          mDate,
                          selectedDate,
                          currentDate,
                          selectedPeriod,
                          limits,
                          isMonthly,
                          allowBeyondLimit
                        )}
                        start={isStart(mDate, selectedPeriod, isMonthly)}
                        end={isEnd(mDate, selectedPeriod, isMonthly)}
                      >
                        {month}
                      </Month>
                    );
                  })}
                </Row>
              ))}
            </div>
          ) : (
            <div>
              <Row>
                {map(getWeekdays(locale, true), (label, i) => (
                  <WeekDay key={i}>{label}</WeekDay>
                ))}
              </Row>
              {map(range(NUMBER_OF_LINE), (i) => (
                <Row week={isWeekly} key={i} onClick={() => selectWeek(i)}>
                  {map(range(DAYS_IN_WEEK), (j) => {
                    const day = next(startDate, i * DAYS_IN_WEEK + j, "day");
                    return (
                      <Day
                        key={j}
                        onClick={() => onClickDay(day)}
                        state={getStateFromDate(
                          day,
                          selectedDate,
                          currentDate,
                          selectedPeriod,
                          limits,
                          isMonthly,
                          allowBeyondLimit
                        )}
                        start={isStart(day, selectedPeriod, isMonthly)}
                        end={isEnd(day, selectedPeriod, isMonthly)}
                      >
                        {day.date()}
                      </Day>
                    );
                  })}
                </Row>
              ))}
            </div>
          )}
        </Body>
      </Picker>
    </Root>
  );
};

export default UiKitPeriodSelectorCalendar;
